import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, GraduationCap, Video, FileText, Eye, Tag, Play, ChevronRight, Calendar, BookOpen, Award, Filter, X, ChevronDown } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import GatedContent from '@/components/GatedContent';
import PDFViewer from '@/components/PDFViewer';
import { API } from '@/App';

const SUBJECTS = [
  'Cas Cliniques', 'Entretien', 'Kératocône', 'Lentilles Hybrides',
  'Lentilles Colorées', 'Lentilles Sclérales', 'Lentilles Souples', 'LRPG',
  'Myopie', 'Organisation', 'Orthokératologie', 'Pathologies',
  'Pédiatrie', 'Presbytie', 'Résumés congrès'
];

const ARTICLE_TYPES = [
  { label: 'Les rapports', value: 'Les Rapports', icon: FileText },
  { label: 'Les publications scientifiques', value: 'Les Publications', icon: BookOpen },
  { label: 'Les cours', value: 'Les Cours', icon: BookOpen },
  { label: 'Les replays', value: 'Les Replays', icon: Play },
  { label: 'Le grand prix de contactologie', value: 'Grand Prix', icon: Award },
];

const CATEGORY_COLORS = {
  'Les Rapports': { bg: 'from-amber-500 to-orange-600', accent: '#f59e0b' },
  'Les Publications': { bg: 'from-emerald-500 to-teal-600', accent: '#10b981' },
  'Les Cours': { bg: 'from-blue-500 to-indigo-600', accent: '#3b82f6' },
  'Les Replays': { bg: 'from-violet-500 to-purple-600', accent: '#8b5cf6' },
  'Grand Prix': { bg: 'from-rose-500 to-pink-600', accent: '#f43f5e' },
};

const FormationSidebar = ({ formations, currentCategory, navigate }) => {
  const years = [...new Set(formations.map(f => f.year).filter(Boolean))].sort((a, b) => b - a);
  const authors = [...new Set(formations.map(f => f.instructor).filter(Boolean))].sort();

  const goWithFilter = (params) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/nos-formations?${qs}`);
  };

  return (
    <div className="space-y-4">
      {/* Périodes */}
      {years.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
          <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les périodes</h3>
          <div className="flex flex-wrap gap-2">
            {years.map(y => (
              <button key={y} onClick={() => goWithFilter({ year: y })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#35AFE4] hover:bg-[#eaf6fc] transition-all"
                data-testid={`sidebar-year-${y}`}>
                <Calendar size={11} /> {y}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sujets */}
      <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
        <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les sujets</h3>
        <ul className="space-y-0.5">
          {SUBJECTS.map(s => (
            <li key={s}>
              <button onClick={() => goWithFilter({ subject: s })}
                className="w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-[#35AFE4] transition-colors flex items-center gap-2"
                data-testid={`sidebar-subject-${s}`}>
                <FileText size={11} className="text-[#35AFE4]/60 flex-shrink-0" /> {s}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Articles de formation */}
      <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
        <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les articles de formation</h3>
        <ul className="space-y-0.5">
          {ARTICLE_TYPES.map(t => {
            const Icon = t.icon;
            const isActive = currentCategory === t.value;
            return (
              <li key={t.label}>
                <button onClick={() => goWithFilter({ cat: t.value })}
                  className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 ${isActive ? 'bg-[#eaf6fc] text-[#35AFE4] font-bold' : 'text-gray-600 hover:bg-gray-50 hover:text-[#35AFE4]'}`}
                  data-testid={`sidebar-cat-${t.label}`}>
                  <Icon size={12} className="text-[#35AFE4]/60 flex-shrink-0" /> {t.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Auteurs */}
      {authors.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#d0e8f0] p-5">
          <h3 className="text-base font-bold text-[#35AFE4] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Les auteurs</h3>
          <div className="space-y-0.5 max-h-[200px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {authors.map(a => (
              <button key={a} onClick={() => goWithFilter({ author: a })}
                className="w-full text-left px-3 py-1 text-xs text-gray-500 hover:text-[#35AFE4] transition-colors"
                data-testid={`sidebar-author-${a}`}>
                {a}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileFilterBar = ({ formations, currentCategory, navigate }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');
  const years = [...new Set(formations.map(f => f.year).filter(Boolean))].sort((a, b) => b - a);
  const authors = [...new Set(formations.map(f => f.instructor).filter(Boolean))].sort();

  const goWithFilter = (params) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/nos-formations?${qs}`);
    setOpen(false);
  };

  return (
    <div className="lg:hidden mb-6">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-[#d0e8f0] rounded-xl text-sm font-semibold text-[#35AFE4] hover:bg-[#eaf6fc] transition-colors"
        data-testid="mobile-filter-toggle">
        <span className="flex items-center gap-2"><Filter size={15} /> Explorer les formations</span>
        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-2 bg-white border border-[#d0e8f0] rounded-2xl overflow-hidden shadow-lg" data-testid="mobile-filter-panel">
          {/* Tabs */}
          <div className="flex border-b border-gray-100 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {[
              { key: 'categories', label: 'Catégories' },
              { key: 'subjects', label: 'Sujets' },
              { key: 'years', label: 'Périodes' },
              { key: 'authors', label: 'Auteurs' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-shrink-0 px-4 py-2.5 text-xs font-semibold transition-colors ${activeTab === tab.key ? 'text-[#35AFE4] border-b-2 border-[#35AFE4]' : 'text-gray-400'}`}
                data-testid={`mobile-tab-${tab.key}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-4 max-h-[300px] overflow-y-auto">
            {activeTab === 'categories' && (
              <div className="space-y-1">
                {ARTICLE_TYPES.map(t => {
                  const Icon = t.icon;
                  const isActive = currentCategory === t.value;
                  return (
                    <button key={t.label} onClick={() => goWithFilter({ cat: t.value })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${isActive ? 'bg-[#eaf6fc] text-[#35AFE4] font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <Icon size={14} className="text-[#35AFE4]/60 flex-shrink-0" /> {t.label}
                    </button>
                  );
                })}
              </div>
            )}
            {activeTab === 'subjects' && (
              <div className="space-y-1">
                {SUBJECTS.map(s => (
                  <button key={s} onClick={() => goWithFilter({ subject: s })}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-[#35AFE4] transition-colors flex items-center gap-2">
                    <FileText size={12} className="text-[#35AFE4]/60 flex-shrink-0" /> {s}
                  </button>
                ))}
              </div>
            )}
            {activeTab === 'years' && (
              <div className="flex flex-wrap gap-2">
                {years.map(y => (
                  <button key={y} onClick={() => goWithFilter({ year: y })}
                    className="px-4 py-2 rounded-full text-sm font-semibold text-[#35AFE4] hover:bg-[#eaf6fc] transition-colors">
                    {y}
                  </button>
                ))}
              </div>
            )}
            {activeTab === 'authors' && (
              <div className="space-y-1">
                {authors.map(a => (
                  <button key={a} onClick={() => goWithFilter({ author: a })}
                    className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-[#35AFE4] transition-colors">
                    {a}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PublicFormationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formation, setFormation] = useState(null);
  const [allFormations, setAllFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEpisode, setActiveEpisode] = useState(0);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/formations/public/${id}`),
      axios.get(`${API}/formations/public`),
    ]).then(([detailRes, listRes]) => {
      setFormation(detailRes.data);
      setAllFormations(listRes.data);
    }).catch(() => navigate('/nos-formations'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen" style={{ fontFamily: "'Roboto', sans-serif", background: 'linear-gradient(180deg, #e8f4f8 0%, #f0f8fb 50%, #f8fcfd 100%)' }}>
        <SiteHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" />
        </div>
      </div>
    );
  }

  if (!formation) return null;

  const catColor = CATEGORY_COLORS[formation.category] || { bg: 'from-[#1e3a5f] to-[#35AFE4]', accent: '#35AFE4' };
  const levelLabels = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Roboto', sans-serif", background: 'linear-gradient(180deg, #e8f4f8 0%, #f0f8fb 50%, #f8fcfd 100%)' }} data-testid="public-formation-detail">
      <SiteHeader />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 pt-6 pb-2">
        <div className="flex items-center gap-2 text-[11px] text-gray-400">
          <button onClick={() => navigate('/nos-formations')} className="hover:text-[#35AFE4] transition-colors font-medium" data-testid="back-btn">
            Formations
          </button>
          <span>/</span>
          {formation.category && (
            <>
              <button onClick={() => navigate(`/nos-formations?cat=${encodeURIComponent(formation.category)}`)} className="hover:text-[#35AFE4] transition-colors font-medium">
                {formation.category}
              </button>
              <span>/</span>
            </>
          )}
          <span className="text-gray-600 font-medium truncate max-w-[400px]">{formation.title}</span>
        </div>
      </div>

      {/* Mobile filter bar */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <MobileFilterBar formations={allFormations} currentCategory={formation.category} navigate={navigate} />
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-10">
        <div className="flex gap-8">
          {/* ===== SIDEBAR (desktop) ===== */}
          <aside className="w-64 flex-shrink-0 hidden lg:block" data-testid="sidebar-filters">
            <div className="sticky top-20">
              <FormationSidebar formations={allFormations} currentCategory={formation.category} navigate={navigate} />
            </div>
          </aside>

          {/* ===== MAIN CONTENT ===== */}
          <main className="flex-1 min-w-0">
            {/* Formation header card */}
            <div className="bg-white rounded-2xl border border-[#d0e8f0] overflow-hidden shadow-sm mb-6" data-testid="formation-header">
              {/* Category gradient banner */}
              <div className={`relative h-32 sm:h-40 bg-gradient-to-r ${catColor.bg} overflow-hidden`}>
                <div className="absolute inset-0 flex items-center justify-center opacity-10">
                  <GraduationCap size={120} className="text-white" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/20 to-transparent" />
                <div className="absolute bottom-4 left-6 flex flex-wrap gap-2">
                  {formation.category && <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full">{formation.category}</span>}
                  {formation.level && <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full">{levelLabels[formation.level] || formation.level}</span>}
                  {formation.year && <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full">{formation.year}</span>}
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1e3a5f] mb-3 leading-tight" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="formation-title">
                  {formation.title}
                </h1>
                {formation.description && (
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 max-w-3xl">{formation.description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                  {formation.instructor && (
                    <span className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${catColor.bg} flex items-center justify-center text-[10px] font-bold text-white`}>
                        {formation.instructor[0]}
                      </div>
                      <span className="font-medium text-gray-600">{formation.instructor}</span>
                    </span>
                  )}
                  {formation.duration_minutes && <span className="flex items-center gap-1"><Clock size={13} /> {Math.round(formation.duration_minutes / 60)}h</span>}
                  {formation.created_at && <span className="flex items-center gap-1"><Calendar size={13} /> {new Date(formation.created_at).toLocaleDateString('fr-FR')}</span>}
                  <span className="flex items-center gap-1" data-testid="formation-views"><Eye size={13} /> {formation.views || 0} vue{(formation.views || 0) > 1 ? 's' : ''}</span>
                  {formation.tags && formation.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {formation.tags.slice(0, 4).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-[#eaf6fc] text-[#35AFE4] text-[10px] font-semibold rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Gated content */}
            <GatedContent>
              <div className="space-y-6">
                {/* Episodes Player (for Replays) */}
                {formation.episodes && formation.episodes.length > 0 && (
                  <div className="space-y-5" data-testid="episodes-section">
                    {/* Video Player */}
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#d0e8f0]">
                      <div className="aspect-video bg-gray-900">
                        {(() => {
                          const ep = formation.episodes[activeEpisode];
                          if (!ep) return null;
                          const videoUrl = ep.video_url || '';
                          const isYoutube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
                          const isMp4 = videoUrl.endsWith('.mp4') || videoUrl.includes('.mp4');
                          if (isYoutube) {
                            const ytId = ep.youtube_id || videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1] || '';
                            return <iframe src={`https://www.youtube.com/embed/${ytId}?rel=0`} className="w-full h-full" allowFullScreen title={ep.title} />;
                          }
                          if (isMp4) {
                            return (
                              <video key={ep.id || activeEpisode} controls controlsList="nodownload" onContextMenu={e => e.preventDefault()} className="w-full h-full" autoPlay data-testid="video-player"
                                onError={e => {
                                  const parent = e.target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:rgba(255,255,255,0.7);font-family:Roboto,sans-serif;gap:8px"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg><p style="font-size:14px;font-weight:600">Vidéo indisponible</p><p style="font-size:11px;opacity:0.5">Le contenu vidéo n\'est pas encore hébergé localement</p></div>';
                                  }
                                }}>
                                <source src={videoUrl} type="video/mp4" />
                              </video>
                            );
                          }
                          return <div className="flex items-center justify-center h-full text-white/60">Format video non supporte</div>;
                        })()}
                      </div>
                      {/* Now playing info bar */}
                      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-white">
                        <div>
                          <p className="font-bold text-gray-900 text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>
                            {formation.episodes[activeEpisode]?.title}
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            Episode {activeEpisode + 1} sur {formation.episodes.length}
                            {formation.episodes[activeEpisode]?.duration ? ` · ${formation.episodes[activeEpisode].duration}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setActiveEpisode(Math.max(0, activeEpisode - 1))} disabled={activeEpisode === 0}
                            className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-20 text-gray-600">
                            <ChevronRight size={18} className="rotate-180" />
                          </button>
                          <button onClick={() => setActiveEpisode(Math.min(formation.episodes.length - 1, activeEpisode + 1))} disabled={activeEpisode >= formation.episodes.length - 1}
                            className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-20 text-gray-600">
                            <ChevronRight size={18} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Episode List */}
                    <div className="bg-white rounded-2xl shadow-sm border border-[#d0e8f0] overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>
                          {formation.episodes.length} episodes
                        </h3>
                        <span className="text-xs text-gray-400">Cliquez pour lire</span>
                      </div>
                      <div className="divide-y divide-gray-50 max-h-[450px] overflow-y-auto">
                        {[...formation.episodes].sort((a, b) => (a.order || 0) - (b.order || 0)).map((ep, i) => (
                          <button
                            key={ep.id || i}
                            onClick={() => setActiveEpisode(i)}
                            className={`w-full text-left px-4 py-3 flex items-center gap-4 transition-all ${
                              activeEpisode === i ? 'bg-[#eaf6fc]' : 'hover:bg-gray-50'
                            }`}
                            data-testid={`episode-${i}`}
                          >
                            <span className={`text-base font-bold flex-shrink-0 w-6 text-center ${activeEpisode === i ? 'text-gray-900' : 'text-gray-300'}`}>{i + 1}</span>
                            <div className={`relative flex-shrink-0 w-24 h-14 rounded-lg overflow-hidden ${activeEpisode === i ? 'ring-2' : ''}`} style={activeEpisode === i ? { '--tw-ring-color': catColor.accent } : {}}>
                              <div className={`absolute inset-0 bg-gradient-to-br ${catColor.bg} flex items-center justify-center`}>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${activeEpisode === i ? 'bg-white/40' : 'bg-white/20'}`}>
                                  {activeEpisode === i ? (
                                    <div className="flex gap-0.5 items-end">
                                      <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" />
                                      <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} />
                                      <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
                                    </div>
                                  ) : (
                                    <Play size={12} className="text-white ml-0.5" fill="white" />
                                  )}
                                </div>
                              </div>
                              {ep.duration && (
                                <span className="absolute bottom-0.5 right-0.5 px-1 py-px bg-black/60 text-white text-[8px] font-mono rounded">{ep.duration}</span>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm leading-snug line-clamp-2 ${activeEpisode === i ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{ep.title}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {ep.duration && <span className="text-[10px] text-gray-400 flex items-center gap-1"><Clock size={9} /> {ep.duration}</span>}
                                {activeEpisode === i && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: catColor.accent }}>En cours</span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Single Video */}
                {!formation.episodes?.length && formation.video_url && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#d0e8f0]" data-testid="video-section">
                    <div className="flex items-center gap-2 mb-4">
                      <Video size={20} className="text-[#35AFE4]" />
                      <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Video</h2>
                    </div>
                    <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                      {formation.video_url.includes('youtube') || formation.video_url.includes('youtu.be') ? (
                        <iframe src={formation.video_url.replace('watch?v=', 'embed/')} className="w-full h-full" allowFullScreen title="Vidéo" />
                      ) : formation.video_url.endsWith('.mp4') || formation.video_url.includes('.mp4') ? (
                        <video controls controlsList="nodownload" onContextMenu={e => e.preventDefault()} className="w-full h-full"
                          onError={e => {
                            const p = e.target.parentElement;
                            if (p) p.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:rgba(255,255,255,0.7);font-family:Roboto,sans-serif;gap:8px"><p style="font-size:14px;font-weight:600">Vidéo indisponible</p><p style="font-size:11px;opacity:0.5">Le contenu vidéo n\'est pas encore hébergé localement</p></div>';
                          }}>
                          <source src={formation.video_url} type="video/mp4" />
                        </video>
                      ) : (
                        <div className="flex items-center justify-center h-full text-white/60 text-sm">Vidéo indisponible</div>
                      )}
                    </div>
                  </div>
                )}

                {/* PDF Viewer */}
                {formation.pdf_url && (
                  <div data-testid="pdf-section">
                    <PDFViewer url={formation.pdf_url} title={formation.title} />
                  </div>
                )}

                {/* Content */}
                {formation.content && formation.content.trim() !== '' && (
                  <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#d0e8f0]">
                    <div className="prose max-w-none text-gray-700 leading-relaxed" data-testid="formation-content" dangerouslySetInnerHTML={{ __html: formation.content }} />
                  </div>
                )}
              </div>
            </GatedContent>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PublicFormationDetailPage;
